import type { LocalDate, MusclePriorities, SessionKind, SessionStatus } from '@cadencia/contracts';
import { classifyChange, type ChangeClassification } from '../decisions/change-policy.js';
import { addLocalDays, compareLocalDates, daysBetweenLocal, daysOfWeek } from '../units/local-date.js';
import type { Muscle } from '../catalog/muscles.js';
import { priorityFor } from './priorities.js';

/**
 * Sesiones perdidas y reorganizacion semanal (docs/TRAINING_ENGINE.md §8).
 *
 * La regla que gobierna todo el modulo es la ultima del §8, y va contra el
 * instinto: perder dos o mas sesiones en una semana NO se recupera. Se acepta la
 * semana como de menor volumen y se registra. Recuperarlo todo a base de meter
 * sesiones juntas sube la fatiga sin beneficio claro, y ademas rompe la
 * separacion de 48 h que es lo unico que protege al musculo.
 *
 * Modulo puro: no lee el reloj ni la base de datos (ADR-007). «Hoy» entra como
 * dato.
 */

// ---------------------------------------------------------------------------
// Convenciones declaradas
// ---------------------------------------------------------------------------

/** Separacion minima entre estimulos del mismo musculo (§8: 48 h). */
export const MIN_SEPARATION_DAYS = 2;

/**
 * `48 h` con granularidad de DIA son dos dias de diferencia, no uno.
 *
 * Las sesiones se programan por dia, no por hora: entre el lunes y el martes
 * pueden pasar 20 h o 44 h y el sistema no sabe cual. Exigir dos dias es la
 * unica lectura que garantiza el minimo en todos los casos; exigir uno lo
 * incumpliria la mitad de las veces sin avisar.
 */

/** Series efectivas por debajo de las cuales un musculo es incidental. */
export const SHARED_MUSCLE_MIN_SETS = 4;

/** Suelo de volumen semanal por debajo del cual se redistribuye (§8). */
export const WEEKLY_VOLUME_FLOOR = 0.8;

/** Series de tren inferior que hacen «importante» una sesion a efectos de cardio. */
export const LOWER_BODY_SIGNIFICANT_SETS = 6;

/** Segundos por serie efectiva, para estimar cuanto cabe todavia en una sesion. */
export const SECONDS_PER_SET = 130;

/** Sesiones perdidas recientes que dejan de ser mala suerte (§8). */
export const REPEATED_MISSED_THRESHOLD = 4;

export const LOWER_BODY_MUSCLES: readonly Muscle[] = [
  'cuadriceps',
  'isquios',
  'gluteo',
  'aductores',
  'gemelo',
  'soleo',
];

// ---------------------------------------------------------------------------
// Entradas
// ---------------------------------------------------------------------------

export interface WeekSession {
  id: string;
  name: string;
  scheduledDate: LocalDate;
  status: SessionStatus;
  sessionKind: SessionKind;
  estimatedMinutes: number;
  /** Series efectivas por musculo previstas en la sesion. */
  volumeByMuscle: Readonly<Partial<Record<Muscle, number>>>;
}

export interface ReorganizeInput {
  /** Lunes de la semana. */
  weekStart: LocalDate;
  today: LocalDate;
  sessions: readonly WeekSession[];
  musclePriorities?: MusclePriorities | undefined;
  maxSessionMinutes?: number | undefined;
  minSeparationDays?: number | undefined;
  /**
   * Sesiones perdidas en las semanas anteriores del bloque. No cambia lo que se
   * hace esta semana; sirve para distinguir un imprevisto de un patron.
   */
  recentMissedSessions?: number | undefined;
}

// ---------------------------------------------------------------------------
// Salidas
// ---------------------------------------------------------------------------

export type ReorganizeAction =
  | { kind: 'reschedule'; sessionId: string; from: LocalDate; to: LocalDate; reasonCodes: string[] }
  | { kind: 'accept_lower_volume'; sessionId: string; reasonCodes: string[] };

/** Series que caben todavia en una sesion que si se va a hacer. */
export interface RedistributionSlot {
  sessionId: string;
  /** Series efectivas que caben sin pasar del limite de minutos. */
  extraSets: number;
  /** Musculos a los que darselas, ya ordenados por prioridad. */
  muscles: Muscle[];
}

export interface ReorganizeDecision {
  actions: ReorganizeAction[];
  missedSessionIds: string[];
  /** Volumen que se va a hacer entre el planificado, 0-1. */
  volumeRatio: number;
  belowVolumeFloor: boolean;
  redistribution: RedistributionSlot[];
  /**
   * Perder sesiones de forma repetida no se arregla moviendo dias: pide
   * revisar la estructura del bloque, y eso es otra decision (§8, §10).
   */
  suggestsStructuralReview: boolean;
  reasonCodes: string[];
  change: ChangeClassification;
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

function volumenTotal(session: WeekSession): number {
  return Object.values(session.volumeByMuscle).reduce<number>(
    (total, series) => total + (series ?? 0),
    0,
  );
}

/** Musculos con estimulo real en la sesion, no los que se rozan de paso. */
function musculosEstimulados(session: WeekSession): Muscle[] {
  return (Object.entries(session.volumeByMuscle) as [Muscle, number | undefined][])
    .filter(([, series]) => (series ?? 0) >= SHARED_MUSCLE_MIN_SETS)
    .map(([muscle]) => muscle);
}

function comparteEstimulo(a: WeekSession, b: WeekSession): boolean {
  const deA = new Set(musculosEstimulados(a));
  return musculosEstimulados(b).some((muscle) => deA.has(muscle));
}

/**
 * ¿Es esta una sesion de pierna de las que se pagan al dia siguiente?
 *
 * Se exporta porque la usan DOS reglas: la recolocacion del cardio del §8 y la
 * prescripcion de cardio del modulo `cardio.ts`. Duplicar el umbral haria que
 * un dia dejaran de estar de acuerdo sobre que es una pierna importante.
 */
export function isSignificantLowerBody(
  volumeByMuscle: Readonly<Partial<Record<Muscle, number>>>,
): boolean {
  const series = LOWER_BODY_MUSCLES.reduce(
    (total, muscle) => total + (volumeByMuscle[muscle] ?? 0),
    0,
  );
  return series >= LOWER_BODY_SIGNIFICANT_SETS;
}

function esTrenInferiorImportante(session: WeekSession): boolean {
  return isSignificantLowerBody(session.volumeByMuscle);
}

/** La sesion sigue en pie: ocupa su dia y su volumen cuenta. */
function siguePlanificada(session: WeekSession, today: LocalDate): boolean {
  if (session.status === 'completed' || session.status === 'in_progress') return true;
  return session.status === 'planned' && compareLocalDates(session.scheduledDate, today) >= 0;
}

function estaPerdida(session: WeekSession, today: LocalDate): boolean {
  if (session.status === 'missed') return true;
  return session.status === 'planned' && compareLocalDates(session.scheduledDate, today) < 0;
}

// ---------------------------------------------------------------------------
// Decision
// ---------------------------------------------------------------------------

export function reorganizeWeek(input: ReorganizeInput): ReorganizeDecision {
  const maxSessionMinutes = input.maxSessionMinutes ?? 90;
  const minSeparationDays = input.minSeparationDays ?? MIN_SEPARATION_DAYS;
  const priorities = input.musclePriorities ?? {};

  const perdidas = input.sessions
    .filter((session) => estaPerdida(session, input.today))
    .sort((a, b) => compareLocalDates(a.scheduledDate, b.scheduledDate));

  // Las que siguen en pie ocupan dia y cuentan volumen. Se copia el array
  // porque las reubicaciones lo van modificando: la segunda sesion movida debe
  // respetar la separacion con la primera, no con donde estaba antes.
  const enPie = input.sessions.filter((session) => siguePlanificada(session, input.today));

  const planificado = input.sessions
    .filter((session) => session.status !== 'skipped' && session.status !== 'rescheduled')
    .reduce((total, session) => total + volumenTotal(session), 0);

  const reasonCodes: string[] = [];
  const actions: ReorganizeAction[] = [];

  const repetidas = (input.recentMissedSessions ?? 0) >= REPEATED_MISSED_THRESHOLD;
  if (repetidas) reasonCodes.push('repeated_missed_sessions');

  if (perdidas.length === 0) {
    reasonCodes.push('nothing_missed');
  } else if (perdidas.length >= 2) {
    // Regla explicita del §8. No es resignacion: es que meter dos sesiones
    // recuperadas en los dias que quedan rompe la separacion de 48 h, y el
    // coste en fatiga no lo compensa.
    reasonCodes.push('two_or_more_missed_accept_lower_volume');
    for (const session of perdidas) {
      actions.push({
        kind: 'accept_lower_volume',
        sessionId: session.id,
        reasonCodes: ['two_or_more_missed_in_week'],
      });
    }
  } else {
    const perdida = perdidas[0] as WeekSession;
    const destino = primerDiaValido(perdida, enPie, input, minSeparationDays);

    if (destino === null) {
      reasonCodes.push('no_valid_day_left');
      actions.push({
        kind: 'accept_lower_volume',
        sessionId: perdida.id,
        reasonCodes: ['no_day_with_valid_separation'],
      });
    } else {
      reasonCodes.push('rescheduled_within_week');
      actions.push({
        kind: 'reschedule',
        sessionId: perdida.id,
        from: perdida.scheduledDate,
        to: destino,
        reasonCodes: ['first_day_with_valid_separation'],
      });
      enPie.push({ ...perdida, scheduledDate: destino, status: 'planned' });
    }
  }

  // Segunda pasada: el cardio no debe preceder inmediatamente a un tren
  // inferior importante, tanto si se ha movido algo como si no (§8).
  actions.push(...recolocarCardio(enPie, input, actions));

  const seVaAHacer = enPie.reduce((total, session) => total + volumenTotal(session), 0);
  const volumeRatio = planificado === 0 ? 1 : seVaAHacer / planificado;
  const belowVolumeFloor = volumeRatio < WEEKLY_VOLUME_FLOOR;
  if (belowVolumeFloor) reasonCodes.push('weekly_volume_below_floor');

  const redistribution = belowVolumeFloor
    ? planDeRedistribucion(enPie, perdidas, priorities, maxSessionMinutes)
    : [];

  return {
    actions,
    missedSessionIds: perdidas.map((session) => session.id),
    volumeRatio,
    belowVolumeFloor,
    redistribution,
    suggestsStructuralReview: repetidas,
    reasonCodes,
    // Mover dias dentro de la semana es ajuste menor mientras el volumen total
    // no se mueva mas de un 10 % (§10). Lo que decide no es cuantas sesiones se
    // tocan, sino cuanto entrenamiento se pierde.
    change: classifyChange({ kind: 'week_reorganization', magnitude: 1 - volumeRatio }),
  };
}

/**
 * Primer dia de la semana, a partir de hoy, donde la sesion cabe respetando la
 * separacion minima con todo lo que comparta estimulo.
 */
// ---------------------------------------------------------------------------
// Movimiento a mano
// ---------------------------------------------------------------------------

export interface MoveSessionInput {
  /** Lunes de la semana. */
  weekStart: LocalDate;
  today: LocalDate;
  sessions: readonly WeekSession[];
  sessionId: string;
  toDate: LocalDate;
  minSeparationDays?: number | undefined;
}

export interface MoveSessionCheck {
  allowed: boolean;
  /**
   * Por que NO se puede, si `allowed` es falso. Vacio si se puede.
   *
   * Codigos y no frases: quien pinta decide como se dicen, y el dominio no
   * sabe en que idioma ni en que sitio de la pantalla van a leerse.
   */
  reasonCodes: string[];
  /**
   * Se puede, pero tiene un coste que conviene decir antes de hacerlo.
   *
   * Separado de `reasonCodes` a proposito: un aviso que impide la accion y otro
   * que solo la matiza no pueden salir por el mismo sitio, o se aprende a
   * ignorar los dos.
   */
  warnings: string[];
}

/**
 * ¿Se puede mover esta sesion a este dia? (§8, edicion manual de la semana.)
 *
 * El §3 de `UI_UX_SPEC.md` pide poder arrastrar sesiones entre dias en
 * escritorio. Arrastrar es un gesto; **cuando el gesto es legal es una regla de
 * entrenamiento**, y por eso vive aqui y no en la pantalla: usa los mismos
 * umbrales que la reorganizacion automatica —la separacion de 48 h y que un
 * musculo cuente a partir de `SHARED_MUSCLE_MIN_SETS` series— y si estuvieran
 * escritos dos veces, un dia dejarian de estar de acuerdo.
 *
 * Lo que NO hace: decidir por el usuario. Mover una sesion es una peticion
 * explicita, no una propuesta del motor, asi que las reglas que solo cuestan
 * fatiga salen como `warnings` y no impiden nada. Solo se bloquea lo que
 * dejaria la semana en un estado que el resto del sistema no sabe representar
 * —dos sesiones el mismo dia— o lo que contradice al calendario —mover al
 * pasado, o mover algo que ya se hizo—.
 */
export function canMoveSession(input: MoveSessionInput): MoveSessionCheck {
  const minSeparationDays = input.minSeparationDays ?? MIN_SEPARATION_DAYS;
  const reasonCodes: string[] = [];
  const warnings: string[] = [];

  const sesion = input.sessions.find((fila) => fila.id === input.sessionId);
  if (sesion === undefined) return { allowed: false, reasonCodes: ['session_not_found'], warnings };

  // Lo ya hecho no se mueve: su fecha es cuando ocurrio, no cuando estaba
  // previsto. Cambiarla reescribiria el historial del que sale toda
  // recomendacion futura.
  if (sesion.status === 'completed' || sesion.status === 'in_progress') {
    reasonCodes.push('session_already_started');
  }

  const dias = daysOfWeek(input.weekStart);
  if (!dias.includes(input.toDate)) reasonCodes.push('date_outside_week');
  if (compareLocalDates(input.toDate, input.today) < 0) reasonCodes.push('date_in_past');

  if (input.toDate === sesion.scheduledDate) reasonCodes.push('same_day');

  const otras = input.sessions.filter((fila) => fila.id !== sesion.id);

  if (otras.some((fila) => fila.scheduledDate === input.toDate)) {
    reasonCodes.push('day_occupied');
  }

  // La separacion de 48 h es lo unico que protege al musculo (§8), asi que no
  // es un aviso: es el motivo por el que existe la regla.
  const choca = otras.some(
    (fila) =>
      siguePlanificada(fila, input.today) &&
      comparteEstimulo(fila, sesion) &&
      Math.abs(daysBetweenLocal(fila.scheduledDate, input.toDate)) < minSeparationDays,
  );
  if (choca) reasonCodes.push('insufficient_separation');

  // Avisos: cuestan fatiga, no coherencia.
  if (sesion.sessionKind === 'cardio' && precedeATrenInferior(input.toDate, otras)) {
    warnings.push('cardio_before_lower_body');
  }
  if (
    esTrenInferiorImportante(sesion) &&
    otras.some(
      (fila) =>
        fila.sessionKind === 'cardio' &&
        fila.scheduledDate === addLocalDays(input.toDate, -1),
    )
  ) {
    warnings.push('lower_body_after_cardio');
  }

  return { allowed: reasonCodes.length === 0, reasonCodes, warnings };
}

/** Los dias de la semana a los que esta sesion SI se puede mover. */
export function validMoveDays(
  input: Omit<MoveSessionInput, 'toDate'>,
): LocalDate[] {
  return daysOfWeek(input.weekStart).filter(
    (dia) => canMoveSession({ ...input, toDate: dia }).allowed,
  );
}

function primerDiaValido(
  perdida: WeekSession,
  enPie: readonly WeekSession[],
  input: ReorganizeInput,
  minSeparationDays: number,
): LocalDate | null {
  const ocupados = new Set(enPie.map((session) => session.scheduledDate));

  for (const dia of daysOfWeek(input.weekStart)) {
    if (compareLocalDates(dia, input.today) < 0) continue;
    if (ocupados.has(dia)) continue;

    const chocaConEstimulo = enPie.some(
      (session) =>
        comparteEstimulo(session, perdida) &&
        Math.abs(daysBetweenLocal(session.scheduledDate, dia)) < minSeparationDays,
    );
    if (chocaConEstimulo) continue;

    if (perdida.sessionKind === 'cardio' && precedeATrenInferior(dia, enPie)) continue;

    return dia;
  }
  return null;
}

function precedeATrenInferior(dia: LocalDate, enPie: readonly WeekSession[]): boolean {
  const siguiente = addLocalDays(dia, 1);
  return enPie.some(
    (session) => session.scheduledDate === siguiente && esTrenInferiorImportante(session),
  );
}

/**
 * Mueve el cardio que haya quedado justo antes de un tren inferior importante.
 *
 * Correr fuerte la vispera de una sesion de pierna se paga en la sesion de
 * pierna, no en la carrera: se llega con los isquios cargados y se entrena peor
 * lo que mas cuesta recuperar.
 */
function recolocarCardio(
  enPie: WeekSession[],
  input: ReorganizeInput,
  yaDecididas: readonly ReorganizeAction[],
): ReorganizeAction[] {
  const yaMovidas = new Set(yaDecididas.map((accion) => accion.sessionId));
  const acciones: ReorganizeAction[] = [];

  for (const session of [...enPie]) {
    if (session.sessionKind !== 'cardio') continue;
    if (yaMovidas.has(session.id)) continue;
    if (compareLocalDates(session.scheduledDate, input.today) < 0) continue;
    if (!precedeATrenInferior(session.scheduledDate, enPie)) continue;

    const ocupados = new Set(enPie.map((otra) => otra.scheduledDate));
    const destino =
      daysOfWeek(input.weekStart).find(
        (dia) =>
          compareLocalDates(dia, input.today) >= 0 &&
          !ocupados.has(dia) &&
          !precedeATrenInferior(dia, enPie),
      ) ?? null;

    // Si no hay hueco mejor se queda donde esta: mover el cardio a un dia peor
    // para cumplir una regla de colocacion no arregla nada.
    if (destino === null) continue;

    acciones.push({
      kind: 'reschedule',
      sessionId: session.id,
      from: session.scheduledDate,
      to: destino,
      reasonCodes: ['cardio_before_lower_body'],
    });
    session.scheduledDate = destino;
  }

  return acciones;
}

/**
 * Cuantas series extra caben en lo que queda de semana y a que musculos darlas.
 *
 * Devuelve el PLAN, no lo aplica: quien materializa las sesiones es el
 * repositorio, y el dominio no toca filas. El orden de los musculos si se
 * decide aqui, porque es una regla de programacion: primero los prioritarios
 * (§4.4) y despues los que mas volumen han perdido.
 */
function planDeRedistribucion(
  enPie: readonly WeekSession[],
  perdidas: readonly WeekSession[],
  priorities: MusclePriorities,
  maxSessionMinutes: number,
): RedistributionSlot[] {
  const perdido = new Map<Muscle, number>();
  for (const session of perdidas) {
    for (const [muscle, series] of Object.entries(session.volumeByMuscle) as [
      Muscle,
      number | undefined,
    ][]) {
      perdido.set(muscle, (perdido.get(muscle) ?? 0) + (series ?? 0));
    }
  }

  const orden: Record<string, number> = { high: 0, normal: 1, low: 2 };
  const musculos = [...perdido.entries()]
    .filter(([, series]) => series > 0)
    .sort((a, b) => {
      const prioridad =
        (orden[priorityFor(a[0], priorities)] ?? 1) - (orden[priorityFor(b[0], priorities)] ?? 1);
      return prioridad !== 0 ? prioridad : b[1] - a[1];
    })
    .map(([muscle]) => muscle);

  return enPie
    .map((session) => {
      const minutosLibres = maxSessionMinutes - session.estimatedMinutes;
      const extraSets = Math.max(0, Math.floor((minutosLibres * 60) / SECONDS_PER_SET));
      return { sessionId: session.id, extraSets, muscles: musculos };
    })
    .filter((slot) => slot.extraSets > 0);
}
